<template>
  <v-container class="pa-0">
    <div
      v-if="isLoading"
      class="d-flex justify-center align-center"
      style="height: 100vh;"
    >
      <v-progress-circular
        indeterminate
        color="#2661FF"
        size="80"
      ></v-progress-circular>
    </div>
    <div v-else-if="!configurationVariablesExists">
      <v-row>
        <v-col cols="12">
          <v-card class="custom_card" flat>
            <v-card-title class="justify-center" style="gap: 8px;">
              <h4 class="title text_color font-weight-medium">
                Configurar variables de comunicación de email
              </h4>
            </v-card-title>
            <v-divider />
            <v-card-text>
              <p class="subtitle-1 d-flex justify-center">
                Primero debe configurar las variables de comunicación de email
                para poder visualizar las métricas de las organizaciones y
                cuentas de email.
              </p>
              <v-form
                ref="create-configuration-variables-form"
                lazy-validation
                v-model="createConfigurationVariablesForm.valid"
              >
                <v-row class="mt-4">
                  <v-col
                    v-for="configurationVariable of configurationVariables"
                    :key="configurationVariable.name"
                  >
                    <v-card class="pa-4 custom_card" flat>
                      <v-row no-gutters class="mx-2 mt-4">
                        <v-col
                          v-for="variable of configurationVariable.variables"
                          :key="variable.name"
                          cols="12"
                          class="d-flex justify-start align-center"
                          style="gap: 12px;"
                        >
                          <span class="text_color font-weight-medium mb-6">
                            {{ variable.title }}
                          </span>
                          <v-text-field
                            v-model="variable.value"
                            type="number"
                            :min="0"
                            :max="100"
                            dense
                            outlined
                            prefix="%"
                            class="rounded-lg"
                            style="max-width: 150px;"
                            :rules="rules.variablesRules"
                          />
                          <span class="text_color font-weight-medium mb-6">
                            {{ variable.description }}
                          </span>
                        </v-col>
                      </v-row>
                    </v-card>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" class="d-flex justify-end">
                    <v-btn
                      :disabled="createConfigurationVariablesForm.isLoading"
                      color="#2661FF"
                      class="custom_card elevation-0"
                      @click="createConfigurationVariables"
                    >
                      <span class="white--text">
                        {{ $t('actions.save') }}
                      </span>
                    </v-btn>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>
    <div v-else>
      <v-row class="py-2">
        <v-col cols="12">
          <h2 class="title mt-4">
            {{ $t('communications.metrics.title') }}
          </h2>
        </v-col>
      </v-row>
      <!------------------------------------------>
      <!-- CONFIGURATION VARIABLES --------------->
      <!------------------------------------------>
      <v-row>
        <v-col
          v-for="configurationVariable of configurationVariables"
          :key="configurationVariable.name"
          cols="12"
          lg="6"
        >
          <v-card class="custom_card" flat>
            <v-card-title style="gap: 8px;">
              <v-icon class="text_color">
                {{ configurationVariable.icon }}
              </v-icon>
              <h4 class="subtitle-1 text_color font-weight-medium">
                {{ configurationVariable.title }}
              </h4>
            </v-card-title>
            <v-divider />
            <v-card-text>
              <v-form
                :ref="`form_${configurationVariable.name}`"
                v-model="configurationVariable.validForm"
                lazy-validation
              >
                <v-row no-gutters class="mx-2 mt-1">
                  <v-col
                    v-for="variable of configurationVariable.variables"
                    :key="variable.name"
                    cols="12"
                    class="d-flex justify-start align-center"
                    style="gap: 12px;"
                  >
                    <span class="text_color font-weight-medium mb-6">
                      {{ variable.title }}
                    </span>
                    <v-text-field
                      v-if="!!variable.type && variable.type === 'percent'"
                      v-model="variable.value"
                      type="number"
                      :min="0"
                      :max="100"
                      dense
                      outlined
                      prefix="%"
                      class="rounded-lg"
                      style="max-width: 150px;"
                      :rules="rules.variablesRules"
                    />
                    <v-text-field
                      v-else
                      v-model="variable.value"
                      type="number"
                      :min="0"
                      dense
                      outlined
                      class="rounded-lg"
                      style="max-width: 150px;"
                      :rules="rules.limitsRules"
                    />
                    <span class="text_color font-weight-medium mb-6">
                      {{ variable.description }}
                    </span>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-row no-gutters>
                <v-col
                  cols="11"
                  class="d-flex justify-end align-center mb-2"
                  style="gap: 12px"
                >
                  <v-btn
                    @click="
                      resetConfigurationVariables(configurationVariable.name)
                    "
                    :disabled="configurationVariable.isLoading"
                    color="white"
                    class="custom_card elevation-0"
                  >
                    <span class="text_color font-weight-medium">
                      {{ $t('actions.restart') }}
                    </span>
                  </v-btn>
                  <v-btn
                    @click="
                      saveConfigurationVariables(configurationVariable.name)
                    "
                    :loading="configurationVariable.isLoading"
                    color="#2661FF"
                    class="custom_card elevation-0"
                  >
                    <span class="white--text">
                      {{ $t('actions.save') }}
                    </span>
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
      <!------------------------------------------>
      <!-- DATES --------------------------------->
      <!------------------------------------------>
      <v-row>
        <v-col cols="12" class="d-flex justify-end align-center">
          <DateBar
            :dateRange="dates.dateRange"
            @updateRange="
              (value, start, index) => {
                dates.dateRange = index
                const startDate = $moment(start).format('YYYY-MM-DD')
                const endDate = dates.endDate

                changeDates(null, startDate, endDate)
              }
            "
            format-date="YYYY-MM-DD"
          />
          <DateFilters
            :initialDate="dates.startDate"
            :finalDate="dates.endDate"
            @updateInitialDate="updateDates('startDate', $event)"
            @updateFinalDate="updateDates('endDate', $event)"
          />
        </v-col>
      </v-row>
      <!------------------------------------------>
      <!-- ACCOUNTS LIST ------------------------->
      <!------------------------------------------>
      <v-row>
        <v-col cols="12">
          <v-card class="custom_card" flat>
            <v-card-title style="gap: 8px;">
              <v-icon class="text_color">
                dns
              </v-icon>
              <h2 class="title text_color">
                {{ $t('communications.metrics.accounts.title') }}
              </h2>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="accountsTable.filters.search"
                    :label="$t('actions.search')"
                    dense
                    outlined
                    clearable
                    hide-details
                    class="rounded-lg"
                    @change="fetchAccounts"
                    @click.clear="fetchAccounts"
                  />
                </v-col>
                <!-- <v-col cols="12" md="2">
                  <v-autocomplete
                    v-model="accountsTable.filters.status"
                    :items="['active', 'inactive']"
                    :label="$t('common.status')"
                    dense
                    outlined
                    hide-details
                    class="rounded-lg"
                    @change="fetchAccounts"
                  />
                </v-col> -->
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-data-table
                    :loading="accountsTable.isLoading"
                    :headers="accountsTable.headers"
                    :items="accountsTable.items"
                    :server-items-length="accountsTable.total"
                    :items-per-page="accountsTable.pagination.itemsPerPage"
                    :page="accountsTable.pagination.page"
                    :sort-by="accountsTable.pagination.sortBy"
                    :sort-desc="accountsTable.pagination.sortDesc"
                    @update:options="accountsTable.pagination = $event"
                  >
                    <template v-slot:no-data>
                      <div class="text-center pa-5 my-5">
                        <v-img
                          :src="require('@/assets/svg/box_empty.svg')"
                          height="150"
                          max-height="150px"
                          aspect-ratio="1"
                          contain
                        />
                        <div class="grey--text">
                          {{ $t('subscriptions_report.no_data') }}
                        </div>
                      </div>
                    </template>
                    <template v-slot:item.name="{ item }">
                      <span>
                        {{ item.name }}
                      </span>
                    </template>
                    <template v-slot:item.ips="{ item }">
                      <div v-for="(ip, index) in item.ips" :key="index">
                        {{ ip }}
                      </div>
                    </template>
                    <template v-slot:item.bounceCount="{ item }">
                      <span> {{ item.bounceCount }} </span>
                    </template>
                    <template v-slot:item.complaintCount="{ item }">
                      <span> {{ item.complaintCount }} </span>
                    </template>
                    <template v-slot:item.bounceRate="{ item }">
                      <v-chip
                        :class="
                          getAccountStatusColor(item.bounceRate, 'bounceRate')
                        "
                      >
                        {{ item.bounceRate ? item.bounceRate : 0 }}%
                      </v-chip>
                    </template>
                    <template v-slot:item.complaintRate="{ item }">
                      <v-chip
                        :class="
                          getAccountStatusColor(
                            item.complaintRate,
                            'complaintRate'
                          )
                        "
                      >
                        {{ item.complaintRate ? item.complaintRate : 0 }}%
                      </v-chip>
                    </template>
                    <template v-slot:item.sendPerSecond="{ item }">
                      <span> {{ item.sendPerSecond }} </span>
                    </template>
                  </v-data-table>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <!------------------------------------------>
      <!-- ORGANIZATIONS LIST -------------------->
      <!------------------------------------------>
      <v-row>
        <v-col cols="12">
          <v-card class="custom_card" flat>
            <v-card-title style="gap: 8px;">
              <v-icon class="text_color">
                warning
              </v-icon>
              <h2 class="title text_color">
                {{ $t('communications.metrics.organizations.title') }}
              </h2>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="organizationsTable.filters.search"
                    :label="$t('actions.search')"
                    dense
                    outlined
                    clearable
                    hide-details
                    class="rounded-lg"
                    @change="fetchOrganizations"
                  />
                </v-col>
                <!-- <v-col cols="12" md="2">
                  <v-autocomplete
                    v-model="organizationsTable.filters.status"
                    :items="['active', 'inactive']"
                    :label="$t('common.status')"
                    dense
                    outlined
                    hide-details
                    class="rounded-lg"
                    @change="fetchOrganizations"
                  />
                </v-col>
                <v-col cols="12" md="2">
                  <v-autocomplete
                    v-model="organizationsTable.filters.account"
                    :items="['configuration_set_2', 'configuration_set_1']"
                    :label="
                      $t('communications.metrics.organizations.filters.account')
                    "
                    dense
                    outlined
                    hide-details
                    class="rounded-lg"
                    @change="fetchOrganizations"
                  />
                </v-col>
                <v-col cols="12" md="2">
                  <v-autocomplete
                    v-model="organizationsTable.filters.blast_type"
                    :items="['autoresponders', 'newsletters']"
                    :label="
                      $t(
                        'communications.metrics.organizations.filters.blast_type'
                      )
                    "
                    dense
                    outlined
                    hide-details
                    class="rounded-lg"
                    @change="fetchOrganizations"
                  />
                </v-col> -->
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-data-table
                    :loading="organizationsTable.isLoading"
                    :headers="organizationsTable.headers"
                    :items="organizationsTable.items"
                    :server-items-length="organizationsTable.total"
                    :items-per-page="organizationsTable.pagination.itemsPerPage"
                    :page="organizationsTable.pagination.page"
                    :sort-by="organizationsTable.pagination.sortBy"
                    :sort-desc="organizationsTable.pagination.sortDesc"
                    @update:options="organizationsTable.pagination = $event"
                  >
                    <template v-slot:no-data>
                      <div class="text-center pa-5 my-5">
                        <v-img
                          :src="require('@/assets/svg/box_empty.svg')"
                          height="150"
                          max-height="150px"
                          aspect-ratio="1"
                          contain
                        />
                        <div class="grey--text">
                          {{ $t('subscriptions_report.no_data') }}
                        </div>
                      </div>
                    </template>
                    <template v-slot:item.organization="{ item }">
                      <span>
                        {{ item }}
                      </span>
                    </template>
                    <template v-slot:item.sendEmailAccount.name="{ item }">
                      <span>
                        {{ item.sendEmailAccount.name }}
                      </span>
                    </template>
                    <template v-slot:item.sendCount="{ item }">
                      <span>
                        {{ item.sendCount | valueFormat(item.sendCount) }}
                      </span>
                    </template>
                    <template v-slot:item.bounceCount="{ item }">
                      <span> {{ item.bounceCount }} </span>
                    </template>
                    <template v-slot:item.complaintCount="{ item }">
                      <span> {{ item.complaintCount }} </span>
                    </template>
                    <template v-slot:item.bounceRate="{ item }">
                      <v-chip
                        :class="
                          getAccountStatusColor(item.bounceRate, 'bounceRate')
                        "
                      >
                        {{ item.bounceRate ? item.bounceRate : 0 }}%
                      </v-chip>
                    </template>
                    <template v-slot:item.complaintRate="{ item }">
                      <v-chip
                        :class="
                          getAccountStatusColor(
                            item.complaintRate,
                            'complaintRate'
                          )
                        "
                      >
                        {{ item.complaintRate ? item.complaintRate : 0 }}%
                      </v-chip>
                    </template>
                    <template v-slot:item.actions="{ item }">
                      <div class="d-flex align-center">
                        <v-switch
                          v-model="item.isBlocked"
                          color="#2661FF"
                          @click="toggleBlockOrganization(item.organization.id)"
                        >
                          <span class="white--text">
                            {{
                              $t(
                                'communications.metrics.organizations.headers.block'
                              )
                            }}
                          </span>
                        </v-switch>
                        <v-menu offset-y>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn v-on="on" v-bind="attrs" icon>
                              <v-icon>more_vert</v-icon>
                            </v-btn>
                          </template>
                          <v-list>
                            <v-list-item
                              link
                              @click="
                                openOrganizationDetails(item.organization.id)
                              "
                            >
                              <v-list-item-title>
                                {{ $t('actions.edit') }}
                              </v-list-item-title>
                            </v-list-item>
                          </v-list>
                        </v-menu>
                      </div>
                    </template>
                  </v-data-table>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <OrganizationDetailsDrawer
      v-if="organizationsCommunications.isOrganizationDetailsDrawerOpen"
    />

    <!-- Blocked Accounts -->
    <v-row>
      <v-col cols="12">
        <v-card class="custom_card" flat>
          <v-card-title style="gap: 8px;">
            <v-icon class="text_color">
              dns
            </v-icon>
            <h2 class="title text_color">
              Cuentas bloqueadas
            </h2>
          </v-card-title>
          <v-data-table
            :loading="blockedAccountsTable.isLoading"
            :headers="blockedAccountsTable.headers"
            :items="blockedAccountsTable.items"
            @click:row="selectAccount"
          >
            <template v-slot:no-data>
              <div class="text-center pa-5 my-5">
                <v-img
                  :src="require('@/assets/svg/box_empty.svg')"
                  height="150"
                  max-height="150px"
                  aspect-ratio="1"
                  contain
                />
                <div class="grey--text">
                  {{ $t('subscriptions_report.no_data') }}
                </div>
              </div>
            </template>
            <template v-slot:item.config="{ item }">
              <span>
                {{
                  !!item.config && item.config.blocked ? 'Blocked' : 'Active'
                }}
              </span>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
    <v-navigation-drawer
      v-if="accountToUpdate.open && accountToUpdate.id"
      :value="accountToUpdate.open"
      width="600px"
      right
      fixed
      temporary
      persistent
      style="z-index: 200; transition: 0.5s cubic-bezier(0.4, 0, 0.2, 1)"
      @input="val => (accountToUpdate.open = val)"
    >
      <BaseToolbar
        type-title="icon"
        icon="edit"
        title="Estado de cuenta"
        @close="closeUpdateAccount"
      />

      <v-row>
        <v-col>
          <v-card class="pa-4" flat>
            <v-form ref="accountForm" v-model="formValid">
              <v-container>
                <v-row>
                  <v-col cols="12">
                    <v-text-field
                      label="ID"
                      v-model="accountToUpdate.id"
                      outlined
                      readonly
                    />
                  </v-col>

                  <v-col cols="12">
                    <v-switch
                      v-model="accountToUpdate.blocked"
                      label="Bloqueado"
                      inset
                    />
                  </v-col>

                  <v-col cols="12">
                    <v-text-field
                      label="Blast Credit"
                      v-model="accountToUpdate.blast_credit"
                      outlined
                      type="number"
                      :rules="[
                        v =>
                          (v !== null && v !== '') || 'El valor es obligatorio',
                        v =>
                          (!isNaN(parseFloat(v)) && parseFloat(v) > 0) ||
                          'Debe ser un número positivo',
                        v => v > 0 || 'Debe ser un número positivo'
                      ]"
                      required
                    />
                  </v-col>
                </v-row>

                <v-row justify="end" class="mt-4">
                  <v-btn color="grey" text @click="closeUpdateAccount">
                    Cancelar
                  </v-btn>

                  <v-btn
                    color="primary"
                    class="ml-2"
                    @click="openAccountUpdateDialog"
                  >
                    Guardar
                  </v-btn>
                </v-row>
              </v-container>
            </v-form>
          </v-card>
          <!-- Confirmation Dialog -->
          <v-dialog v-model="showConfirmDialog" max-width="400px">
            <v-card>
              <v-card-title class="text-h6">¿Estás seguro?</v-card-title>
              <v-card-text>
                ¿Deseas guardar los cambios realizados en la cuenta?
              </v-card-text>
              <v-card-actions class="justify-end">
                <v-btn text color="grey" @click="showConfirmDialog = false"
                  >Cancelar</v-btn
                >
                <v-btn color="primary" @click="saveAccountUpdate"
                  >Confirmar</v-btn
                >
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-col>
      </v-row>
    </v-navigation-drawer>
  </v-container>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import DateFilters from '@/components/Common/DateFilters/Index'
import DateBar from '@/components/Common/DateBar/Index'
import OrganizationDetailsDrawer from '../../components/Comunications/Metrics/OrganizationDetailsDrawer.vue'
import BaseToolbar from '@/components/BaseToolbar.vue'

export default {
  name: 'Metrics',
  layout: 'Dashboard',
  components: {
    DateBar,
    DateFilters,
    OrganizationDetailsDrawer,
    BaseToolbar
  },
  data() {
    return {
      createConfigurationVariablesForm: {
        valid: true,
        isLoading: false
      },
      isLoading: false,
      formValid: false, 
      showConfirmDialog: false,
      dates: {
        startDate: this.$moment()
          .startOf('month')
          .format('YYYY-MM-DD'),
        endDate: this.$moment()
          .endOf('day')
          .format('YYYY-MM-DD'),
        dateRange: 3
      },
      configurationVariables: {
        notification: {
          name: 'notification',
          validForm: true,
          title: this.$t('communications.metrics.notification.title'),
          icon: 'warning',
          isLoading: false,
          variables: {
            bounceNotification: {
              title: this.$t('communications.metrics.notification.mnb'),
              description: this.$t(
                'communications.metrics.notification.mnb_description'
              ),
              name: 'bounceNotification',
              value: null,
              type: 'percent'
            },
            complaintNotification: {
              title: this.$t('communications.metrics.notification.mnc'),
              description: this.$t(
                'communications.metrics.notification.mnc_description'
              ),
              name: 'complaintNotification',
              value: null,
              type: 'percent'
            },
            globalLimitNotification: {
              title: 'Global Limit',
              description: '',
              name: 'globalLimitNotification',
              value: null
            },
            blastLimitNotification: {
              title: 'Blast Limit',
              description: '',
              name: 'blastLimitNotification',
              value: null
            }
          }
        },
        block: {
          name: 'block',
          validForm: true,
          title: this.$t('communications.metrics.block.title'),
          icon: 'block',
          isLoading: false,
          variables: {
            bounceBlock: {
              title: this.$t('communications.metrics.block.mmb'),
              description: this.$t(
                'communications.metrics.block.mmb_description'
              ),
              name: 'bounceBlock',
              value: null,
              type: 'percent'
            },
            complaintBlock: {
              title: this.$t('communications.metrics.block.mbc'),
              description: this.$t(
                'communications.metrics.block.mbc_description'
              ),
              name: 'complaintBlock',
              value: null,
              type: 'percent'
            },
            globalLimitBlock: {
              title: 'GlobalLimit',
              description: '',
              name: 'globalLimitBlock',
              value: null
            },
            blastLimitBlock: {
              title: 'Blast Limit',
              description: '',
              name: 'blastLimitBlock',
              value: null
            }
          }
        }
      },
      accountsTable: {
        items: [],
        total: 0,
        isLoading: false,
        pagination: {
          page: 1,
          itemsPerPage: 10,
          sortBy: ['id'],
          sortDesc: [false]
        },
        filters: {
          status: null,
          search: null
        },
        headers: [
          {
            text: this.$t('communications.metrics.accounts.headers.account'),
            value: 'name',
            sortable: false,
            class: 'header_color'
          },
          {
            text: 'IPS',
            value: 'ips',
            sortable: false,
            class: 'header_color'
          },
          {
            text: 'Bounces',
            value: 'bounceCount',
            sortable: false,
            class: 'header_color'
          },
          {
            text: 'Complaints',
            value: 'complaintCount',
            sortable: false,
            class: 'header_color'
          },
          {
            text: '%Bounces',
            value: 'bounceRate',
            class: 'header_color'
          },
          {
            text: '%Complaints',
            value: 'complaintRate',
            class: 'header_color'
          },
          {
            text: this.$t(
              'communications.metrics.accounts.headers.ships_per_second'
            ),
            value: 'sendPerSecond',
            class: 'header_color'
          }
        ]
      },
      blockedAccountsTable: {
        items: [],
        total: 0,
        isLoading: false,
        pagination: {
          page: 1,
          itemsPerPage: 10,
          sortBy: ['id'],
          sortDesc: [false]
        },
        headers: [
          {
            text: 'ID',
            value: 'id',
            class: 'header_color',
            sortable: false
          },
          {
            text: this.$t(
              'communications.metrics.organizations.headers.account'
            ),
            value: 'name',
            class: 'header_color'
          },
          {
            text: 'Blocked',
            value: 'config',
            class: 'header_color'
          },
          {
            text: 'Blast credit',
            value: 'blast_credit',
            class: 'header_color'
          }
        ]
      },
      accountToUpdate: {
        id: null,
        blocked: false,
        blast_credit: 0,
        open: false
      },
      organizationsTable: {
        items: [],
        total: 0,
        isLoading: false,
        pagination: {
          page: 1,
          itemsPerPage: 10,
          sortBy: ['id'],
          sortDesc: [false]
        },
        filters: {
          period: null,
          search: null,
          account: null,
          status: null,
          blast_type: null
        },
        headers: [
          {
            text: this.$t(
              'communications.metrics.organizations.headers.organization'
            ),
            value: 'organization.name',
            class: 'header_color',
            sortable: false
          },
          {
            text: this.$t(
              'communications.metrics.organizations.headers.account'
            ),
            value: 'sendEmailAccount.name',
            class: 'header_color'
          },
          {
            text: this.$t('communications.metrics.organizations.headers.ships'),
            value: 'sendCount',
            class: 'header_color'
          },
          {
            text: 'Bounces',
            value: 'bounceCount',
            class: 'header_color'
          },
          {
            text: 'Complaints',
            value: 'complaintCount',
            class: 'header_color'
          },
          {
            text: '%Bounces',
            value: 'bounceRate',
            class: 'header_color'
          },
          {
            text: '%Complaints',
            value: 'complaintRate',
            class: 'header_color'
          },
          {
            text: this.$t('communications.metrics.organizations.headers.block'),
            value: 'actions',
            class: 'header_color',
            sortable: false
          }
        ]
      },
      rules: {
        variablesRules: [
          v => !!v || this.$t('messages.validation_error.required', {}),
          v =>
            (v && v > 0 && v <= 100) ||
            this.$t('messages.validation_error.range', {
              arg0: 0,
              arg1: 100
            }),
          v =>
            !Number.isNaN(v) || this.$t('messages.validation_error.number', {})
        ],
        limitsRules: [
          v => !!v || this.$t('messages.validation_error.required', {}),
          v =>
            (v && v > 0) || this.$t('messages.validation_error.required', {}),
          v =>
            !Number.isNaN(v) || this.$t('messages.validation_error.number', {})
        ]
      }
    }
  },
  watch: {
    'accountsTable.pagination': {
      async handler() {
        await this.fetchAccounts()
      },
      deep: true
    },
    'organizationsTable.pagination': {
      async handler() {
        await this.fetchOrganizations()
      },
      deep: true
    }
  },
  computed: {
    ...mapState({
      configurationVariablesStore: ({ communicationByOrg }) =>
        communicationByOrg.configurationVariables,
      emailAccounts: ({ communicationByOrg }) => communicationByOrg.accounts,
      organizationsCommunications: ({ communicationByOrg }) =>
        communicationByOrg.organizations
    }),
    configurationVariablesExists() {
      return !!Object.keys(this.configurationVariablesStore).length
    }
  },
  async created() {
    this.isLoading = true
    await this.init()
    this.isLoading = false
  },
  methods: {
    ...mapActions({
      fetchConfigurationVariablesAction:
        'communicationByOrg/fetchConfigurationVariables',
      saveConfigurationVariablesAction:
        'communicationByOrg/saveConfigurationVariables',
      createConfigurationVariablesAction:
        'communicationByOrg/createConfigurationVariables',
      fetchEmailAccounts: 'communicationByOrg/fetchEmailAccounts',
      fetchOrganizationsAction: 'communicationByOrg/fetchOrganizations',
      fetchOrganization: 'communicationByOrg/fetchOrganization',
      toggleBlockOrganizationAction:
        'communicationByOrg/toggleBlockOrganization',
      fetchSendEmailAccountsAction: 'communicationByOrg/fetchSendEmailAccounts'
    }),
    ...mapMutations({
      setOrganizationDetailsDrawer:
        'communicationByOrg/setOrganizationDetailsDrawer'
    }),
    async init() {
      await this.fetchConfigurationVariables()

      if (this.configurationVariablesExists) {
        await this.fetchAccounts()
        await this.fetchOrganizations()
        await this.fetchSendEmailAccounts()
      }

      await this.blockedAccounts()
    },
    async updateDates(property, date) {
      this.dates[property] = date

      const startDate = this.$moment(this.dates.startDate).format('YYYY-MM-DD')
      const endDate = this.$moment(this.dates.endDate)
        .endOf('day')
        .format('YYYY-MM-DD')

      await this.changeDates('customized', startDate, endDate)
    },
    async changeDates(code, start, end) {
      const CUSTOM_DATE = 7

      this.dates.startDate = start

      if (code === 'customized') {
        this.dates.endDate = end
        this.dates.dateRange = CUSTOM_DATE
      }

      await this.init()
    },
    async createConfigurationVariables() {
      const { configurationVariables } = this

      const valid = await this.$refs[
        'create-configuration-variables-form'
      ].validate()

      if (!valid) {
        return
      }

      const payload = {
        bounceNotification: Number(
          configurationVariables.notification.variables['bounceNotification']
            .value
        ),
        complaintNotification: Number(
          configurationVariables.notification.variables['complaintNotification']
            .value
        ),
        bounceBlock: Number(
          configurationVariables.block.variables['bounceBlock'].value
        ),
        complaintBlock: Number(
          configurationVariables.block.variables['complaintBlock'].value
        ),
        globalLimitBlock: Number(
          configurationVariables.block.variables['globalLimitBlock'].value
        ),
        blastLimitBlock: Number(
          configurationVariables.block.variables['blastLimitBlock'].value
        ),
        globalLimitNotification: Number(
          configurationVariables.block.variables['globalLimitNotification']
            .value
        ),
        blastLimitNotification: Number(
          configurationVariables.block.variables['blastLimitNotification'].value
        )
      }

      await this.createConfigurationVariablesAction(payload)
    },
    getAccountStatusColor(value, variable) {
      const [bounceRate, complaintRate] = ['bounceRate', 'complaintRate']
      const [bounceNotification, complaintNotification] = [
        'bounceNotification',
        'complaintNotification'
      ]
      const [bounceBlock, complaintBlock] = ['bounceBlock', 'complaintBlock']

      if (variable === bounceRate) {
        if (
          value >=
          this.configurationVariables.notification.variables[bounceNotification]
            .value
        ) {
          return 'red white--text'
        } else if (
          value >=
          this.configurationVariables.block.variables[bounceBlock].value
        ) {
          return 'orange white--text'
        } else {
          return 'green white--text'
        }
      } else if (variable === complaintRate) {
        if (
          value >=
          this.configurationVariables.notification.variables[
            complaintNotification
          ].value
        ) {
          return 'red white--text'
        } else if (
          value >=
          this.configurationVariables.block.variables[complaintBlock].value
        ) {
          return 'orange white--text'
        } else {
          return 'green white--text'
        }
      }
    },
    // <!------------------------------------------>
    // <!-- CONFIGURATION VARIABLES --------------->
    // <!------------------------------------------>
    async fetchConfigurationVariables() {
      const { configurationVariables } = this
      console.log({
        configurationVariables,
        store: this.configurationVariablesStore
      })
      await this.fetchConfigurationVariablesAction()

      // Set the values of the configuration variables
      if (Object.keys(this.configurationVariablesStore).length) {
        Object.keys(configurationVariables).forEach(configurationVariable => {
          Object.keys(
            configurationVariables[configurationVariable].variables
          ).forEach(variable => {
            if (
              this.configurationVariablesStore[
                configurationVariables[configurationVariable].variables[
                  variable
                ].name
              ]
            ) {
              configurationVariables[configurationVariable].variables[
                variable
              ].value = this.configurationVariablesStore[
                configurationVariables[configurationVariable].variables[
                  variable
                ].name
              ]
            }
          })
        })
      }
    },
    async resetConfigurationVariables(type) {
      const { configurationVariables } = this
      const [NOTIFICATION, BLOCK] = ['notification', 'block']

      const defaultValues = {
        notification: {
          bounceNotification: 1,
          complaintNotification: 0.3,
          globalLimitNotification: 0,
          blastLimitNotification: 0
        },
        block: {
          bounceBlock: 5,
          complaintBlock: 0.5,
          globalLimitBlock: 0,
          blastLimitBlock: 0
        }
      }

      switch (type) {
        case NOTIFICATION: {
          Object.keys(configurationVariables[type].variables).forEach(
            variableKey => {
              const variable =
                configurationVariables[type].variables[variableKey]

              if (
                defaultValues.notification &&
                defaultValues.notification[variable.name]
              ) {
                variable.value = defaultValues.notification[variable.name]
              }
            }
          )

          break
        }
        case BLOCK: {
          Object.keys(configurationVariables[type].variables).forEach(
            variableKey => {
              const variable =
                configurationVariables[type].variables[variableKey]

              if (defaultValues.block && defaultValues.block[variable.name]) {
                variable.value = defaultValues.block[variable.name]
              }
            }
          )
          break
        }
        default:
          break
      }

      await this.saveConfigurationVariables(type)
    },
    async saveConfigurationVariables(type) {
      const { configurationVariables, configurationVariablesStore } = this
      const [NOTIFICATION, BLOCK] = ['notification', 'block']

      const { variables } = configurationVariables[type]

      let payload = {}

      const valid = await this.$refs[`form_${type}`][0].validate()

      if (!valid) {
        return
      }

      switch (type) {
        case NOTIFICATION: {
          payload = {
            id: configurationVariablesStore.id,
            bounceNotification: Number(variables.bounceNotification.value),
            complaintNotification: Number(
              variables.complaintNotification.value
            ),
            globalLimitNotification: Number(
              variables.globalLimitNotification.value
            ),
            blastLimitNotification: Number(
              variables.blastLimitNotification.value
            )
          }

          break
        }
        case BLOCK: {
          payload = {
            id: configurationVariablesStore.id,
            bounceBlock: Number(variables.bounceBlock.value),
            complaintBlock: Number(variables.complaintBlock.value),
            globalLimitBlock: Number(variables.globalLimitBlock.value),
            blastLimitBlock: Number(variables.blastLimitBlock.value)
          }

          break
        }
        default:
          break
      }

      configurationVariables[type].isLoading = true
      await this.saveConfigurationVariablesAction(payload)
      configurationVariables[type].isLoading = false
    },
    // <!------------------------------------------>
    // <!-- ACCCOUNTS LIST ------------------------>
    // <!------------------------------------------>
    async fetchAccounts() {
      const { accountsTable, dates } = this
      const { pagination, filters } = accountsTable

      let sortBy, sortDirection

      if (Array.isArray(pagination.sortBy)) {
        sortBy = pagination.sortBy.join('')
        sortDirection = pagination.sortDesc.at(0) ? 'DESC' : 'ASC'
      }

      const payload = {
        page: pagination.page,
        limit: pagination.itemsPerPage,
        sortBy: [
          {
            key: sortBy,
            order: sortDirection
          }
        ],
        startDate: dates.startDate,
        endDate: dates.endDate,
        status: filters.status,
        search: filters.search
      }

      this.accountsTable.isLoading = true
      await this.fetchEmailAccounts(payload)

      accountsTable.items = this.emailAccounts.items
      accountsTable.total = parseInt(this.emailAccounts.total, 10)

      this.accountsTable.isLoading = false
    },
    // <!------------------------------------------>
    // <!-- ORGANIZATIONS LIST -------------------->
    // <!------------------------------------------>
    async fetchOrganizations() {
      const { organizationsTable, dates } = this
      const { pagination, filters } = organizationsTable

      let sortBy, sortDirection

      if (Array.isArray(pagination.sortBy)) {
        sortBy = pagination.sortBy.join('')
        sortDirection = pagination.sortDesc.at(0) ? 'DESC' : 'ASC'
      }

      const payload = {
        page: pagination.page,
        limit: pagination.itemsPerPage,
        sortBy: [
          {
            key: sortBy,
            order: sortDirection
          }
        ],
        startDate: dates.startDate,
        endDate: dates.endDate,
        period: filters.period,
        search: filters.search,
        account: filters.account,
        status: filters.status,
        blast_type: filters.blast_type
      }

      this.organizationsTable.isLoading = true
      await this.fetchOrganizationsAction(payload)

      organizationsTable.items = this.organizationsCommunications.items
      organizationsTable.total = parseInt(
        this.organizationsCommunications.total,
        10
      )

      this.organizationsTable.isLoading = false
    },
    async toggleBlockOrganization(organizationId) {
      const payload = {
        organizationId
      }

      await this.toggleBlockOrganizationAction(payload)
    },
    async openOrganizationDetails(organizationId) {
      await this.fetchOrganization(organizationId)
      this.setOrganizationDetailsDrawer(true)
    },
    async fetchSendEmailAccounts() {
      await this.fetchSendEmailAccountsAction()
    },
    async blockedAccounts() {
      try {
        this.blockedAccountsTable.isLoading = true
        const blocked = (await axios.get(`admin/blocked_accounts`)).data

        console.log({ blocked })

        this.blockedAccountsTable.items = blocked
      } catch (e) {
        console.log({ e })
        throw new Error(`e: ${e.message}`)
      }

      this.blockedAccountsTable.isLoading = false
    },
    async selectAccount(item, event) {
      this.showConfirmDialog = false
      this.accountToUpdate.id = item.id
      this.accountToUpdate.blocked = item.config ? !!item.config.blocked : false
      this.accountToUpdate.blast_credit = item.blast_credit
        ? item.blast_credit
        : 0

      this.accountToUpdate.open = true
    },
    async closeUpdateAccount() {
      this.accountToUpdate.id = null
      this.accountToUpdate.blocked = false
      this.accountToUpdate.blast_credit = 0

      this.accountToUpdate.open = false
      this.showConfirmDialog = false
    },
    async saveAccountUpdate() {
      if (!this.$refs.accountForm.validate()) {
        return
      }

      this.showConfirmDialog = false;

      try {
        const payload = {
          id: this.accountToUpdate.id,
          blocked: this.accountToUpdate.blocked,
          blast_credit: this.accountToUpdate.blast_credit
        }

        const response = await axios.put('admin/blocked_accounts', payload)

        if (response.data.success) {
          this.closeUpdateAccount()
          this.blockedAccounts()
        } else {
          console.log('Error al actualizar la cuenta')
        }
      } catch (error) {
        console.error('Error al actualizar la cuenta:', error)
      }
    },
    openAccountUpdateDialog() {
      if (this.$refs.accountForm.validate()) {
        this.showConfirmDialog = true;
      }
    },
  }
}
</script>

<style lang="scss">
.custom_card {
  border-radius: 12px !important;
  border: 1px solid #d0d3db !important;
}

.text_color {
  color: #344054 !important;
}

.header_color {
  color: #2661ff !important;
}
</style>
